/**
 * Playbook-styled markdown renderer.
 *
 * The strategy-doc agent emits a Campaign Launch Playbook in markdown with
 * a specific convention: the executive-summary hero stats are written as a
 * three-line blockquote of `**VALUE** — LABEL` lines. This component
 * upgrades a vanilla `ReactMarkdown` render with:
 *
 *   - A cover header band (the H1 and the report's date; the legacy
 *     "Prepared for / Source inputs" blockquote is dropped, see splitCover).
 *   - Hero stat cards (the 3-line blockquote in §1).
 *   - Tight section dividers between numbered top-level sections.
 *
 * Source-of-truth stays markdown; this is presentation only.
 */

import React, { useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText } from 'lucide-react';
import { splitCover } from '@/lib/strategy/report-cover';

interface PlaybookRendererProps {
  content: string;
  title?: string;
}

const HERO_LINE = /^\*\*(.+?)\*\*\s*[—-]\s*(.+)$/;

function HeroStats({ lines }: { lines: string[] }) {
  const items = lines
    .map((raw) => {
      const m = raw.match(HERO_LINE);
      return m ? { value: m[1], label: m[2] } : null;
    })
    .filter((x): x is { value: string; label: string } => x !== null);
  if (items.length === 0) return null;
  return (
    <div className='my-6 grid gap-3 sm:grid-cols-3'>
      {items.map((item, idx) => (
        <div
          key={idx}
          className='rounded-lg border border-[#C4B5FD] bg-[#EDE9FE] p-4 text-center'
        >
          <p className='text-2xl font-bold text-[#290BD9]'>{item.value}</p>
          <p className='mt-1 text-xs uppercase tracking-wide text-gray-700'>
            {item.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function isHeroBlockquote(children: React.ReactNode): string[] | null {
  /**
   * react-markdown gives blockquote children as an array of nodes; the cheap
   * path is to flatten to text and pattern-match. Hero blockquotes look like:
   *
   * > **€2,000** — MONTHLY BUDGET across 3 campaigns
   * > **4/mo** — TARGET paying customers
   * > **~1 mo** — PAYBACK, LTV ≈ 48 months
   *
   * Three lines, each starting with `**…**`. The cover blockquote that
   * follows the H1 ("Prepared for / Source inputs / Audit sidecar") doesn't
   * match because those lines are `**Prepared for:** …`, not `**…** —`.
   */
  const flatten = (node: React.ReactNode): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(flatten).join('');
    if (node && typeof node === 'object' && 'props' in node) {
      const props = (node as { props: { children?: React.ReactNode } }).props;
      return flatten(props.children);
    }
    return '';
  };
  const text = flatten(children).trim();
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2 || lines.length > 4) return null;
  const allHero = lines.every((l) => HERO_LINE.test(l));
  return allHero ? lines : null;
}

export function PlaybookRenderer({ content, title }: PlaybookRendererProps) {
  const { body, dateLabel } = useMemo(() => splitCover(content), [content]);
  const components: Components = useMemo(
    () => ({
      h1: ({ children }) => (
        // On screen: negative margins bleed the cobalt band to the pane
        // edges (the detail pane's frame clips it — the playbook has no
        // card of its own). In print: `print:m-0` cancels them so the band
        // sits flush at the top of page 1 (otherwise -mt-6 pushes the band
        // above the page boundary and page 1 renders blank).
        <div
          data-cover-band
          className='-mx-6 -mt-6 mb-8 bg-[#290BD9] px-8 py-10 text-white print:m-0'
        >
          <div className='flex items-center gap-3 text-xs uppercase tracking-widest text-white/70'>
            <FileText className='h-4 w-4' />
            GlitchAds
          </div>
          <h1 className='mt-3 text-3xl font-bold leading-tight'>{children}</h1>
          {dateLabel && <p className='mt-2 text-sm text-white/70'>{dateLabel}</p>}
        </div>
      ),
      h2: ({ children }) => (
        <h2 className='mt-10 border-b border-gray-200 pb-2 text-2xl font-bold text-gray-900'>
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className='mt-6 text-lg font-semibold text-gray-900'>{children}</h3>
      ),
      blockquote: ({ children }) => {
        const hero = isHeroBlockquote(children);
        if (hero) return <HeroStats lines={hero} />;
        return (
          // whitespace-pre-line: the markdown keeps each "**Label:** value"
          // on its own source line (soft breaks); honour them on screen. The
          // parser also emits literal '\n' nodes around the inner <p> — with
          // pre-line those become blank lines, so drop them and the p margin.
          <blockquote className='my-4 whitespace-pre-line border-l-4 border-[#290BD9] bg-gray-50 px-4 py-3 text-sm text-gray-700 [&_p]:my-0'>
            {React.Children.toArray(children).filter((c) => c !== '\n')}
          </blockquote>
        );
      },
      ul: ({ children }) => (
        <ul className='my-3 ml-6 list-disc space-y-1 text-gray-800'>{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className='my-3 ml-6 list-decimal space-y-1 text-gray-800'>{children}</ol>
      ),
      p: ({ children }) => (
        <p className='my-3 leading-relaxed text-gray-800'>{children}</p>
      ),
      table: ({ children }) => (
        <div className='my-4 overflow-x-auto'>
          <table className='w-full border-collapse text-sm'>{children}</table>
        </div>
      ),
      th: ({ children }) => (
        <th className='border border-gray-300 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-900'>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className='border border-gray-300 px-3 py-2 align-top text-gray-800'>
          {children}
        </td>
      ),
      code: ({ children }) => (
        <code className='rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800'>
          {children}
        </code>
      ),
    }),
    [dateLabel],
  );

  return (
    <>
      {/*
        Print stylesheet for the browser's own print (Cmd+P): these rules
        hide everything except the playbook node and let it bleed to the
        page edges so the cobalt header band looks right. Export uses the
        jsPDF stack themed to this rendering instead (see the viewer).
      */}
      <style>{`
        @media print {
          /* 0. Force backgrounds and colors to print (browsers strip them by
             default to save ink). Without this the cobalt header band and
             the hero stat cards in §1 render as outlines only. */
          #strategy-doc-playbook,
          #strategy-doc-playbook * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* 1. Hide every direct body child (display:none removes layout
             space entirely, unlike visibility:hidden which would leave a
             blank gap before the playbook). */
          body > * { display: none !important; }
          /* 2. Re-show the chain of ancestors that contains the playbook
             — every body-level Radix portal stays visible so the dialog
             tree leading down to #strategy-doc-playbook can render. */
          body > div:has(#strategy-doc-playbook) { display: block !important; }
          body > div:has(#strategy-doc-playbook) * { display: revert !important; }
          /* ...but only the chain itself and the playbook's own subtree: on
             the Reports page the same root holds the sidebar, the report
             list and the toolbar, and none of that belongs in the PDF. */
          body > div:has(#strategy-doc-playbook) *:not(:has(#strategy-doc-playbook)):not(#strategy-doc-playbook):not(#strategy-doc-playbook *) {
            display: none !important;
          }
          /* Re-hide siblings inside the dialog tree that aren't the
             playbook itself (e.g. the toolbar, the Radix dialog close
             button). The display:revert rule above would otherwise
             un-hide the X-button rendered inside DialogContent — these
             selectors override that. */
          body > div:has(#strategy-doc-playbook) [data-print-hidden],
          [data-radix-dialog-content] > button[type="button"],
          [data-radix-dialog-content] [aria-label="Close"] {
            display: none !important;
          }

          /* 3. Strip layout constraints from every ancestor between body and
             the playbook. The dialog enforces max-h: 90vh + overflow: auto
             on screen, which would otherwise clip print to the visible
             viewport. We also drop transforms so the document doesn't get
             centred via translate() and pushed off the page. */
          html, body,
          [data-radix-portal],
          [role="dialog"],
          [data-radix-dialog-overlay],
          [data-radix-dialog-content],
          [data-state="open"] {
            position: static !important;
            transform: none !important;
            max-height: none !important;
            max-width: none !important;
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            box-shadow: none !important;
            border: none !important;
            inset: auto !important;
          }

          #strategy-doc-playbook {
            /* Padding pushes content away from page edges. We can't use
               @page margin here because that re-enables the browser's
               default headers/footers (date, URL, page count). Setting
               @page margin to 0 strips those, then we provide our own
               inset via container padding. */
            padding: 16mm !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
            background: white !important;
          }

          /* The on-screen H1 uses negative margins (-mx-6 -mt-6) to bleed
             the cobalt band to the card edges. In print, that pulls content
             above the page top and leaves the first page blank. Reset
             those margins so the band sits at the top of page 1. */
          #strategy-doc-playbook > div:first-child {
            margin: 0 !important;
            border-radius: 0 !important;
          }

          /* 4. Hide the in-dialog toolbar (Download button) when printing. */
          [data-print-hidden] { display: none !important; }

          /* 5. Avoid awkward breaks inside cards and headers. */
          h1, h2, h3 { page-break-after: avoid; break-after: avoid; }
          table { page-break-inside: avoid; break-inside: avoid; }

          /* Zero @page margin strips the browser-injected header (date,
             window title) and footer (URL, page count) in Chrome and
             Safari. Container padding above provides the visual inset. */
          @page { margin: 0; size: auto; }
        }
      `}</style>
      <div
        id='strategy-doc-playbook'
        className='bg-white px-6 py-6'
      >
        {title ? (
          <p className='text-xs uppercase tracking-widest text-gray-500'>{title}</p>
        ) : null}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {body}
        </ReactMarkdown>
      </div>
    </>
  );
}
